import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, TextFieldProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getAuthToken } from '../../lib/authToken';

export interface CaseAutocompleteOption {
  id: string;
  caseNumber?: string;
  title?: string;
  clientId?: string;
  client?: {
    id?: string;
    firstName?: string;
    lastName?: string;
  };
  [key: string]: unknown;
}

interface CaseAutocompleteProps {
  value: CaseAutocompleteOption | string | null;
  onChange: (caseOption: CaseAutocompleteOption | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  textFieldProps?: Partial<TextFieldProps>;
  /** Extra query params (e.g. status=open) */
  extraParams?: Record<string, string>;
}

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
});

const labelFor = (option: CaseAutocompleteOption) => {
  const num = option.caseNumber || '';
  const title = option.title || '';
  const client =
    option.client
      ? ` — ${[option.client.firstName, option.client.lastName].filter(Boolean).join(' ')}`
      : '';
  return `${num}${num && title ? ' — ' : ''}${title}${client}`.trim() || option.id;
};

/**
 * Server-search case picker (Wave P.1). Debounced `?search=` against GET /api/cases.
 */
const CaseAutocomplete: React.FC<CaseAutocompleteProps> = ({
  value,
  onChange,
  label = 'Case',
  placeholder = 'Search cases…',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  textFieldProps,
  extraParams,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(inputValue.trim()), 250);
    return () => clearTimeout(t);
  }, [inputValue]);

  const selectedId = typeof value === 'string' ? value : value?.id || null;

  const { data: selectedCase } = useQuery({
    queryKey: ['case-autocomplete-selected', selectedId],
    enabled: Boolean(selectedId) && (typeof value === 'string' || !value?.caseNumber),
    queryFn: async (): Promise<CaseAutocompleteOption | null> => {
      const res = await fetch(`/api/cases/${selectedId}`, { headers: authHeaders() });
      if (!res.ok) return null;
      const json = await res.json();
      return (json.data?.case || json.data) as CaseAutocompleteOption;
    },
    staleTime: 60_000,
  });

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['case-autocomplete', debounced, extraParams],
    queryFn: async (): Promise<CaseAutocompleteOption[]> => {
      const params = new URLSearchParams({ limit: '20', ...(extraParams || {}) });
      if (debounced) params.set('search', debounced);
      const res = await fetch(`/api/cases?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data?.cases || []) as CaseAutocompleteOption[];
    },
    staleTime: 15_000,
  });

  const resolvedValue = useMemo(() => {
    if (!selectedId) return null;
    if (typeof value === 'object' && value?.id) return value;
    const fromOptions = options.find((o) => o.id === selectedId);
    if (fromOptions) return fromOptions;
    if (selectedCase?.id === selectedId) return selectedCase;
    return { id: selectedId, title: 'Loading…' } as CaseAutocompleteOption;
  }, [selectedId, value, options, selectedCase]);

  const mergedOptions = useMemo(() => {
    if (!resolvedValue?.id) return options;
    if (options.some((o) => o.id === resolvedValue.id)) return options;
    return [resolvedValue, ...options];
  }, [options, resolvedValue]);

  return (
    <Autocomplete
      options={mergedOptions}
      value={resolvedValue}
      onChange={(_, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset' && resolvedValue) {
          setInputValue(labelFor(resolvedValue));
          return;
        }
        setInputValue(next);
      }}
      getOptionLabel={(option) => labelFor(option)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(x) => x}
      loading={isFetching}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default CaseAutocomplete;
